import { useCallback, useMemo, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const TOTAL_HOURS = 24;
const START_ANGLE = -130;
const END_ANGLE = 130;
const VIEWBOX_CENTER = 120;
const ARC_RADIUS = 90;

const clampHours = (hours) => Math.max(Math.min(hours, TOTAL_HOURS), 0);

const polarToCartesian = (cx, cy, radius, angleDeg) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180.0;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
};

const describeArc = (cx, cy, radius, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');
};

export default function DowntimeGaugeChart() {
  const { theme } = useTheme();
  const [downtime, setDowntime] = useState(0);
  const gradientId = useMemo(
    () => `downtime-arc-${Math.random().toString(36).slice(2, 8)}`,
    []
  );

  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(`/api/equipment_status.php?latest=1&_=${Date.now()}`);
      const json = await res.json();
      json.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const latest = json[0];
      if (latest) {
        const parseHours = (start, end) => {
          if (!start || !end) return 0;
          const s = new Date(`1970-01-01T${start}Z`);
          const e = new Date(`1970-01-01T${end}Z`);
          const diff = (e - s) / 3600000;
          return diff > 0 ? diff : 0;
        };
        const planned = parseHours(
          latest.planned_downtime_start,
          latest.planned_downtime_end
        );
        const unplanned = parseHours(
          latest.unplanned_downtime_start,
          latest.unplanned_downtime_end
        );
        setDowntime(planned + unplanned);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useAutoRefresh(fetchLatest, []);

  const clamped = clampHours(downtime);
  const progress = clamped / TOTAL_HOURS;
  const progressAngle = START_ANGLE + (END_ANGLE - START_ANGLE) * progress;
  const trackPath = describeArc(
    VIEWBOX_CENTER,
    VIEWBOX_CENTER,
    ARC_RADIUS,
    START_ANGLE,
    END_ANGLE
  );
  const progressPath = describeArc(
    VIEWBOX_CENTER,
    VIEWBOX_CENTER,
    ARC_RADIUS,
    START_ANGLE,
    progressAngle
  );

  const trackColor = theme === 'dark' ? 'rgba(148,163,184,0.25)' : 'rgba(148,163,184,0.35)';
  const needleColor = theme === 'dark' ? '#e2e8f0' : '#0f172a';

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="relative aspect-square w-full max-w-[260px]">
        <svg viewBox="0 0 240 240" className="h-full w-full">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="50%" stopColor="#fbbf24" />
              <stop offset="75%" stopColor="#fb923c" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          <path
            d={trackPath}
            fill="none"
            stroke={trackColor}
            strokeWidth="12"
            strokeLinecap="round"
          />
          <path
            d={progressPath}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth="12"
            strokeLinecap="round"
          />
          <circle cx="120" cy="120" r="4" fill={needleColor} />
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
          <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
            Downtime
          </span>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-semibold text-white">{clamped.toFixed(1)}h</span>
            <span className="text-xs text-slate-400">/ 24h</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 backdrop-blur-md dark:bg-slate-900/70">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Healthy
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 backdrop-blur-md dark:bg-slate-900/70">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-300" /> Watch
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 backdrop-blur-md dark:bg-slate-900/70">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-400" /> Critical
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
